/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Zod schemas for the vault envelope and its inner payload. Every object
 * is `.strict()` so unknown fields are rejected — the format is closed.
 * Bumping the schema means bumping `version` (outer) or `schemaVersion`
 * (inner) and writing a new parser; the old parser is frozen forever.
 *
 * One deliberate exception (#247): a purely *additive* inner field with a
 * `.default()` does not require a `schemaVersion` bump — envelopes written
 * before the field existed still parse (the default fills in), and
 * envelopes written after still parse under the same literal version.
 * `symmetricKeys` is the first such field.
 */

import { z } from 'zod';
import { base64nopad } from '@scure/base';

export const ENVELOPE_VERSION = 1;
export const VAULT_CONTENTS_SCHEMA_VERSION = 1;

export const KDF_ALGORITHM = 'argon2id' as const;
export const CIPHER_ALGORITHM = 'xchacha20-poly1305' as const;

const base64nopadBytes = z.string().transform((s, ctx) => {
  try {
    return base64nopad.decode(s);
  } catch {
    ctx.addIssue({
      code: 'custom',
      message: 'invalid base64nopad encoding',
    });
    return z.NEVER;
  }
});

const KdfBlockSchema = z
  .object({
    algorithm: z.literal(KDF_ALGORITHM),
    salt: base64nopadBytes,
    memoryKiB: z.number().int().min(8),
    iterations: z.number().int().min(1),
    parallelism: z.number().int().min(1),
  })
  .strict();

const CipherBlockSchema = z
  .object({
    algorithm: z.literal(CIPHER_ALGORITHM),
    nonce: base64nopadBytes,
  })
  .strict();

/**
 * Outer envelope. Only `wrappedKey`, `wrappedKeyNonce`, and `ciphertext`
 * carry encrypted/wrapped material; everything else is the authenticated
 * header that AAD covers (see `aad.ts`).
 */
export const EnvelopeOuterSchema = z
  .object({
    version: z.literal(ENVELOPE_VERSION),
    envelopeId: z.string().min(1),
    kdf: KdfBlockSchema,
    cipher: CipherBlockSchema,
    wrappedKey: base64nopadBytes,
    wrappedKeyNonce: base64nopadBytes,
    ciphertext: base64nopadBytes,
    counter: z.number().int().min(0),
  })
  .strict();

export type EnvelopeOuter = z.infer<typeof EnvelopeOuterSchema>;

/**
 * Serialized form of the outer envelope — the JSON shape with base64
 * strings instead of Uint8Array. Use this for `JSON.stringify` output
 * and for transport.
 */
export interface EnvelopeOuterSerialized {
  version: number;
  envelopeId: string;
  kdf: {
    algorithm: typeof KDF_ALGORITHM;
    salt: string;
    memoryKiB: number;
    iterations: number;
    parallelism: number;
  };
  cipher: {
    algorithm: typeof CIPHER_ALGORITHM;
    nonce: string;
  };
  wrappedKey: string;
  wrappedKeyNonce: string;
  ciphertext: string;
  counter: number;
}

/* --- Inner payload --- */

// Strict ISO 8601 datetime (Zod 4 built-in). `Date.parse` was the
// previous fence-post and is implementation-defined — `"May 6, 2026"`
// or `"6 May 2026"` slipped through, with cross-browser-ambiguous
// results once the encrypted vault traveled between devices. All
// production callers funnel through `nowIso()` → `new Date().toISOString()`,
// which emits the `…Z` canonical form `z.iso.datetime()` accepts, so
// this is a no-op for emitted data and a tighter fence for everything
// else (see #119).
export const IsoDateString = z.iso.datetime();

const IdentitySchema = z
  .object({
    id: z.string().min(1),
    // Lifecycle states — see `keystate.ts` for the transition graph.
    // `pending` is generated-but-not-yet-active; `destroyed` is terminal
    // and indicates private-key bytes have been zeroed (audit trail kept).
    state: z.enum(['pending', 'active', 'retiring', 'retired', 'destroyed']),
    createdAt: IsoDateString,
    notAfter: IsoDateString.optional(),
    kem: z
      .object({
        algorithm: z.literal('ML-KEM-768'),
        publicKey: base64nopadBytes,
        privateKey: base64nopadBytes,
      })
      .strict(),
    sig: z
      .object({
        algorithm: z.literal('ML-DSA-65'),
        publicKey: base64nopadBytes,
        privateKey: base64nopadBytes,
      })
      .strict(),
  })
  .strict();

const PinnedContactSchema = z
  .object({
    identity: z.string().min(1),
    fingerprint: base64nopadBytes,
    firstSeen: IsoDateString,
    algorithm: z.string().min(1),
  })
  .strict();

/** Byte length of a document key (DK). Matches `document/encrypt.ts`. */
export const SYMMETRIC_KEY_BYTES = 32;

/**
 * A stored symmetric document key (DK) — #247 / F13-F14 DK lifecycle.
 * `id` doubles as the F13 `documentId` (a UUID in production), so a
 * recipient-side unwrap can find the DK that encrypted a given document.
 * The key bytes live only inside the encrypted envelope; the serialized
 * form is base64nopad like every other byte field.
 */
const SymmetricKeySchema = z
  .object({
    id: z.string().min(1),
    algorithm: z.literal('xchacha20-poly1305'),
    key: base64nopadBytes.refine(
      bytes => bytes.length === SYMMETRIC_KEY_BYTES,
      {
        error: issue =>
          `symmetric key must be ${SYMMETRIC_KEY_BYTES} bytes, got ${(issue.input as Uint8Array).length}`,
      }
    ),
    createdAt: IsoDateString,
  })
  .strict();

const AuditEventSchema = z
  .object({
    at: IsoDateString,
    kind: z.enum(['created', 'rotated', 'retired', 'pinned', 'unlocked']),
    details: z.record(z.string(), z.unknown()),
  })
  .strict();

export const VaultContentsSchema = z
  .object({
    schemaVersion: z.literal(VAULT_CONTENTS_SCHEMA_VERSION),
    envelopeId: z.string().min(1),
    createdAt: IsoDateString,
    updatedAt: IsoDateString,
    identities: z.array(IdentitySchema),
    contactKeys: z.array(PinnedContactSchema),
    // #247: additive with a default — pre-#247 envelopes parse to [].
    symmetricKeys: z.array(SymmetricKeySchema).default([]),
    auditLog: z.array(AuditEventSchema),
  })
  .strict();

export type VaultContents = z.infer<typeof VaultContentsSchema>;
export type Identity = z.infer<typeof IdentitySchema>;
export type PinnedContact = z.infer<typeof PinnedContactSchema>;
export type StoredSymmetricKey = z.infer<typeof SymmetricKeySchema>;
export type AuditEvent = z.infer<typeof AuditEventSchema>;

/**
 * Encode a parsed `VaultContents` (with Uint8Array bytes) back to its
 * JSON-serializable form (with base64nopad strings). Inverse of the Zod
 * schema's transform direction.
 */
export const serializeVaultContents = (
  contents: VaultContents
): Record<string, unknown> => ({
  schemaVersion: contents.schemaVersion,
  envelopeId: contents.envelopeId,
  createdAt: contents.createdAt,
  updatedAt: contents.updatedAt,
  identities: contents.identities.map(id => ({
    id: id.id,
    state: id.state,
    createdAt: id.createdAt,
    ...(id.notAfter !== undefined ? { notAfter: id.notAfter } : {}),
    kem: {
      algorithm: id.kem.algorithm,
      publicKey: base64nopad.encode(id.kem.publicKey),
      privateKey: base64nopad.encode(id.kem.privateKey),
    },
    sig: {
      algorithm: id.sig.algorithm,
      publicKey: base64nopad.encode(id.sig.publicKey),
      privateKey: base64nopad.encode(id.sig.privateKey),
    },
  })),
  contactKeys: contents.contactKeys.map(c => ({
    identity: c.identity,
    fingerprint: base64nopad.encode(c.fingerprint),
    firstSeen: c.firstSeen,
    algorithm: c.algorithm,
  })),
  // `?? []` guards legacy JS fixtures constructed before #247 added the
  // field; typed callers always carry the array (schema default).
  symmetricKeys: (contents.symmetricKeys ?? []).map(k => ({
    id: k.id,
    algorithm: k.algorithm,
    key: base64nopad.encode(k.key),
    createdAt: k.createdAt,
  })),
  auditLog: contents.auditLog.map(a => ({
    at: a.at,
    kind: a.kind,
    details: a.details,
  })),
});
